import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import AttachmentPanel from "./AttachmentPanel";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import {
  ArrowLeft, FileStack, CheckCircle2, Ban, Send, PackageCheck, Unlock, Lock,
  Wallet, ArrowLeftRight, PhoneCall, History, Paperclip, Pencil, Trash2, PlusCircle,
} from "lucide-react";
import { CN_BASE, StatusPill, AgingPill, Modal, Field, inputCls } from "./FinanceCreditNote";

// Status tempat pencairan/pemakaian boleh dicatat — SAMA PERSIS dengan
// `STATUS_BOLEH_SETTLEMENT` di `backend/routers/credit_note.py`. Kalau daftar
// itu berubah di backend, tombol "Catat Pencairan/Pemakaian" di sini harus
// disesuaikan juga (murni gerbang tampilan; backend tetap menolak sendiri
// kalau ada yang lolos).
const STATUS_BOLEH_SETTLEMENT = ["menunggu_supplier", "siap_cair", "partially_used"];

const STEP_DEFS = [
  { key: "draft", label: "Dibuat" },
  { key: "dokumen_dibuat", label: "Dokumen Lengkap" },
  { key: "diserahkan", label: "Diserahkan" },
  { key: "menunggu_supplier", label: "Diproses Supplier" },
  { key: "siap_cair", label: "Disetujui" },
  { key: "cair_terpakai", label: "Cair / Terpakai" },
  { key: "closed", label: "Closed" },
];
// Indeks langkah untuk tiap status backend. `dokumen_diserahkan` sengaja tidak
// muncul di sini — status itu transien (lihat docstring `credit_note.py`),
// begitu `menunggu_supplier` tercapai node "Diserahkan" & "Diproses Supplier"
// otomatis sama-sama "selesai" karena keduanya di-set dalam satu panggilan API.
const STEP_INDEX = { draft: 0, dokumen_dibuat: 1, menunggu_supplier: 3, siap_cair: 4, partially_used: 5, cair: 5, terpakai: 5, closed: 6 };
const METODE_OPTIONS = ["Telepon", "WhatsApp", "Email", "Kunjungan", "Lainnya"];

export default function CreditNoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const cabangOptions = useCabangOptions({ denganKosong: true });

  const [cn, setCn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const [tab, setTab] = useState("overview");
  const [modalKey, setModalKey] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [showAtt, setShowAtt] = useState(false);
  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [busy, setBusy] = useState(false);

  const canUpdate = can("finance_credit_note", "update");
  const canDelete = can("finance_credit_note", "delete");
  // `reopen` di backend TIDAK memakai require_permission sama sekali — dibatasi
  // langsung ke role CEO/Admin/Superadmin (izin lebih sempit dari tier "delete"
  // biasa). Gerbang tampilan di sini mengikuti PERSIS supaya tidak ada tombol
  // yang berujung 403.
  const canReopen = ["finance_ceo", "admin", "superadmin"].includes(user?.role);

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const r = await api.get(`${CN_BASE}/${id}`);
        if (!batal) setCn(r.data.item);
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat Credit Note"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [id, reload]);

  useEffect(() => {
    api.get(`${CN_BASE}/kategori-options`).then((r) => setKategoriOpts(r.data.kategori || [])).catch(() => {});
  }, []);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || okMsg);
      bump();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); return false; }
    finally { setBusy(false); }
  };

  const doBuatDokumen = () => act(() => api.post(`${CN_BASE}/${id}/buat-dokumen`), "Dokumen ditandai dibuat ✓");
  const doSiapCair = () => act(() => api.post(`${CN_BASE}/${id}/siap-cair`, {}), "Ditandai siap cair ✓");
  const doHapus = async () => {
    if (!window.confirm(`Hapus draft ${cn.code}? Aksi ini tidak bisa dibatalkan.`)) return;
    const ok = await act(() => api.delete(`${CN_BASE}/${id}`), "Credit Note dihapus");
    if (ok) navigate("/finance/credit-note");
  };

  if (loading || loadError || !cn) {
    return (
      <div className="p-4 md:p-8">
        <LoadingState loading={loading} error={loadError} onRetry={bump} varian="kartu" jumlahKartu={3} data-testid="cn-detail-loading" />
      </div>
    );
  }

  // Asal dokumen dihitung backend dari pointer sisi dokumen ASAL (batch cashback
  // / retur pembelian), bukan dari medan CN yang bisa diisi lewat body.
  const asalCashback = cn._asal_cashback || [];
  const asalRetur = cn._asal_retur || null;
  const returSah = !!(asalRetur && asalRetur.sah);
  // Sisa pemilihan kompensasi retur yang gagal/diubah: backend menolak Serahkan
  // (409) — satu-satunya tindakan yang masuk akal adalah menghapus draft-nya.
  const returSisa = !!(asalRetur && !asalRetur.sah);
  const bertaut = asalCashback.length > 0 || returSah;
  // Gerbang Batalkan SAMA dengan `batalkan_credit_note`: izin hapus CN; CN hasil
  // settlement Cashback ikut mem-void settlement batch-nya, jadi butuh izin hapus
  // Cashback Claim juga; CN kompensasi retur 'kredit DOA' ditolak (dibatalkan
  // lewat menu Retur); tertaut ke >1 settlement = data ganda, ditolak 409.
  // Batch cashback milik program Price Protection yang sudah SELESAI: backend
  // menolak (409) karena uangnya dibekukan bersama snapshot (tinjauan PP r1).
  const bolehBatalkanCN = canDelete && !returSah && asalCashback.length <= 1 &&
    (asalCashback.length === 0 || can("finance_cashback_claim", "delete")) &&
    !asalCashback.some((a) => a.uang_terkunci_alasan);
  const bolehLihatCashback = can("finance_cashback_claim", "view");

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-credit-note-detail">
      <button onClick={() => navigate("/finance/credit-note")} data-testid="cn-back"
              className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Credit Note
      </button>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-start justify-between flex-col md:flex-row gap-3">
          <div>
            <p className="font-mono text-xs text-slate-500">{cn.code}</p>
            <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
              <FileStack className="w-6 h-6 text-emerald-600" /> {cn.supplier_nama}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{cn.kategori}{cn.brand ? ` · ${cn.brand}` : ""}{cn.cabang ? ` · ${cn.cabang}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={cn.status} />
            <AgingPill aging={cn._aging} overdue={cn.overdue} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <StatBox label="Nilai Awal" value={idr(cn.nilai)} />
          <StatBox label="Cair" value={idr(cn.nominal_cair)} tone="text-emerald-700" />
          <StatBox label="Terpakai" value={idr(cn.nominal_terpakai)} tone="text-emerald-700" />
          <StatBox label="Outstanding" value={idr(cn.outstanding)} tone="text-slate-900" big />
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100">
          <ProgressStepper status={cn.status} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          {cn.status === "draft" && canUpdate && (
            <ActionBtn icon={PackageCheck} label="Buat Dokumen" onClick={doBuatDokumen} testid="cn-act-buatdok" disabled={busy} />
          )}
          {["draft", "dokumen_dibuat"].includes(cn.status) && canUpdate && !returSisa && (
            <ActionBtn icon={Send} label="Serahkan ke Supplier" onClick={() => setModalKey("serahkan")} testid="cn-act-serahkan" primary />
          )}
          {cn.status === "menunggu_supplier" && canUpdate && (
            <ActionBtn icon={CheckCircle2} label="Tandai Siap Cair" onClick={doSiapCair} testid="cn-act-siapcair" disabled={busy} />
          )}
          {STATUS_BOLEH_SETTLEMENT.includes(cn.status) && canUpdate && (
            <>
              <ActionBtn icon={Wallet} label="Catat Pencairan" onClick={() => setModalKey("cair")} testid="cn-act-cair" primary />
              <ActionBtn icon={ArrowLeftRight} label="Catat Pemakaian" onClick={() => setModalKey("terpakai")} testid="cn-act-terpakai" />
            </>
          )}
          {["cair", "terpakai"].includes(cn.status) && canDelete && (
            <ActionBtn icon={Lock} label="Tutup (Close)" onClick={() => setModalKey("close")} testid="cn-act-close" />
          )}
          {cn.status === "closed" && canReopen && (
            <ActionBtn icon={Unlock} label="Buka Kembali" onClick={() => setModalKey("reopen")} testid="cn-act-reopen" />
          )}
          {!["closed", "dibatalkan"].includes(cn.status) && canUpdate && (
            <ActionBtn icon={Pencil} label="Edit" onClick={() => setModalKey("edit")} testid="cn-act-edit" />
          )}
          {["draft", "dokumen_dibuat", "menunggu_supplier", "siap_cair"].includes(cn.status)
            && (cn.nominal_cair || 0) <= 0.5 && (cn.nominal_terpakai || 0) <= 0.5 && bolehBatalkanCN && (
            <ActionBtn icon={Ban} label="Batalkan" onClick={() => setModalKey("batalkan")} testid="cn-act-batalkan" tone="rose" />
          )}
          {/* CN bertaut tidak boleh sekadar hilang: backend menolak Hapus (CN
              cashback → pakai Batalkan; CN retur → ubah kompensasi di menu Retur). */}
          {["draft", "dokumen_dibuat"].includes(cn.status) && canDelete && !bertaut && (
            <ActionBtn icon={Trash2} label="Hapus" onClick={doHapus} testid="cn-act-hapus" tone="rose" disabled={busy} />
          )}
        </div>

        <InfoAsal cn={cn} asalCashback={asalCashback} asalRetur={asalRetur} bolehLihatCashback={bolehLihatCashback}
                  navigate={navigate} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="border-b border-slate-100 flex items-center gap-1 px-2 overflow-x-auto">
          {[
            { key: "overview", label: "Overview" },
            { key: "documents", label: "Documents" },
            { key: "settlement", label: "Settlement" },
            { key: "usage", label: "Usage" },
            { key: "followup", label: "Follow-up" },
            { key: "activity", label: "Activity Log" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cn-tab-${t.key}`}
                    className={`px-4 h-11 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "overview" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="No. Referensi Supplier" value={cn.no_referensi || "-"} />
              <InfoRow label="Kategori" value={cn.kategori} />
              <InfoRow label="Tanggal Terbit" value={formatTanggal(cn.tanggal_terbit)} />
              <InfoRow label="Tanggal Dokumen Dibuat" value={cn.tanggal_dokumen_dibuat ? formatTanggal(cn.tanggal_dokumen_dibuat) : "-"} />
              <InfoRow label="Tanggal Diserahkan" value={cn.tanggal_diserahkan ? formatTanggal(cn.tanggal_diserahkan) : "-"} />
              <InfoRow label="Expected Settlement" value={cn.expected_settlement_date ? formatTanggal(cn.expected_settlement_date) : "-"} />
              <InfoRow label="Dibuat oleh" value={`${cn.created_by_name || cn.created_by || "-"} · ${formatTanggal(cn.created_at, { denganJam: true })}`} col2 />
              {cn.keterangan && <InfoRow label="Keterangan" value={cn.keterangan} col2 />}
              {cn.status === "dibatalkan" && (
                <div className="md:col-span-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="cn-cancel-info">
                  <p className="font-semibold">Dibatalkan oleh {cn.batal_by} pada {cn.batal_at ? formatTanggal(cn.batal_at, { denganJam: true }) : "-"}</p>
                  <p className="mt-1">{cn.batal_reason}</p>
                </div>
              )}
            </div>
          )}

          {tab === "documents" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Unggah bukti dokumen CN (dari supplier, konfirmasi cair, dsb). Setelah dokumen lengkap, gunakan
                tombol <b>Serahkan ke Supplier</b> di atas untuk submit & posting jurnal.
              </p>
              <button onClick={() => setShowAtt(true)} data-testid="cn-open-attachments"
                      className="h-10 px-4 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Kelola Dokumen Lampiran
              </button>
            </div>
          )}

          {tab === "settlement" && (
            <SettlementTable cn={cn} tipe="cair" title="Riwayat Pencairan" emptyMsg="Belum ada pencairan tercatat."
                             canUpdate={canUpdate} canDelete={canDelete}
                             onAdd={() => setModalKey("cair")} onVoid={(s) => setVoidTarget(s)} />
          )}
          {tab === "usage" && (
            <SettlementTable cn={cn} tipe="terpakai" title="Riwayat Pemakaian (Potong Utang)" emptyMsg="Belum ada pemakaian tercatat."
                             canUpdate={canUpdate} canDelete={canDelete}
                             onAdd={() => setModalKey("terpakai")} onVoid={(s) => setVoidTarget(s)} />
          )}

          {tab === "followup" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-base text-slate-900">Riwayat Follow-up</p>
                {canUpdate && !["closed", "dibatalkan"].includes(cn.status) && (
                  <button onClick={() => setModalKey("followup")} data-testid="cn-add-followup"
                          className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                    <PlusCircle className="w-4 h-4" /> Tambah Follow-up
                  </button>
                )}
              </div>
              {(cn.follow_ups || []).length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">Belum ada follow-up tercatat.</p>
              ) : (
                <div className="space-y-2">
                  {[...(cn.follow_ups || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((fu) => (
                    <div key={fu.id} data-testid={`cn-fu-row-${fu.id}`} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <p className="text-sm font-medium text-slate-900">{formatTanggal(fu.tanggal)} · {fu.metode}</p>
                        <p className="text-xs text-slate-500">PIC: {fu.pic}</p>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{fu.hasil}</p>
                      {fu.next_follow_up_date && <p className="text-xs text-amber-700 mt-1">Follow-up berikutnya: {formatTanggal(fu.next_follow_up_date)}</p>}
                      {fu.catatan && <p className="text-xs text-slate-500 mt-1">{fu.catatan}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">oleh {fu.by_name || fu.by} · {formatTanggal(fu.at, { denganJam: true })}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-2" data-testid="cn-activity-log">
              {[...(cn.riwayat_status || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((r, i) => (
                <div key={i} data-testid={`cn-log-row-${i}`} className="flex items-start gap-3 border-b border-slate-50 pb-2 last:border-0">
                  <History className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill status={r.status} />
                      <span className="text-xs text-slate-500">{formatTanggal(r.at, { denganJam: true })}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">oleh {r.by}{r.catatan ? ` — ${r.catatan}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAtt && (
        <AttachmentPanel module="credit_note" objId={cn.id} objLabel={`${cn.code} · ${cn.supplier_nama}`}
                          onClose={() => { setShowAtt(false); bump(); }} />
      )}

      {modalKey === "serahkan" && (
        <SerahkanModal cn={cn} asalRetur={returSah ? asalRetur : null}
                       onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "cair" && (
        <CairModal cn={cn} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "terpakai" && (
        <TerpakaiModal cn={cn} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "followup" && (
        <FollowUpModal cn={cn} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "close" && (
        <CloseModal cn={cn} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "edit" && (
        <EditModal cn={cn} kategoriOpts={kategoriOpts} cabangOptions={cabangOptions} bertaut={bertaut}
                   onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "batalkan" && (
        <ReasonModal title="Batalkan Credit Note" icon={Ban} label="Alasan pembatalan" submitLabel="Batalkan CN"
                     testid="cn-batalkan-modal" onClose={() => setModalKey(null)}
                     onSubmit={async (reason) => {
                       const ok = await act(() => api.post(`${CN_BASE}/${id}/batalkan`, { reason }), "Credit Note dibatalkan ✓");
                       if (ok) setModalKey(null);
                     }} />
      )}
      {modalKey === "reopen" && (
        <ReasonModal title="Buka Kembali Credit Note" icon={Unlock} label="Alasan membuka kembali" submitLabel="Buka Kembali"
                     testid="cn-reopen-modal" onClose={() => setModalKey(null)}
                     onSubmit={async (reason) => {
                       const ok = await act(() => api.post(`${CN_BASE}/${id}/reopen`, { reason }), "Credit Note dibuka kembali ✓");
                       if (ok) setModalKey(null);
                     }} />
      )}
      {voidTarget && (
        <ReasonModal title="Void Settlement" icon={Ban} label="Alasan void" submitLabel="Void Settlement"
                     testid="cn-void-modal" onClose={() => setVoidTarget(null)}
                     onSubmit={async (reason) => {
                       const ok = await act(() => api.post(`${CN_BASE}/${id}/settlement/${voidTarget.id}/void`, { reason }), "Settlement di-void ✓");
                       if (ok) setVoidTarget(null);
                     }} />
      )}
    </div>
  );
}

// ============ Header sub-komponen ============
function StatBox({ label, value, tone, big }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display font-bold mt-0.5 ${big ? "text-xl" : "text-lg"} ${tone || "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid, primary, tone, disabled }) {
  const cls = tone === "rose" ? "border-rose-200 text-rose-700 hover:bg-rose-50"
    : primary ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
    : "border-slate-200 text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 ${cls}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function ProgressStepper({ status }) {
  if (status === "dibatalkan") {
    return (
      <div className="flex items-center gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm font-medium" data-testid="cn-cancelled-banner">
        <Ban className="w-4 h-4" /> Credit Note ini DIBATALKAN — alur ditutup di luar urutan normal.
      </div>
    );
  }
  const currentIdx = STEP_INDEX[status] ?? 0;
  const idx5Done = ["cair", "terpakai", "closed"].includes(status);
  return (
    <div className="flex items-start overflow-x-auto pb-1" data-testid="cn-progress-stepper">
      {STEP_DEFS.map((s, i) => {
        let state;
        if (i < currentIdx) state = "done";
        else if (i === currentIdx) state = (i === 5 && idx5Done) ? "done" : "current";
        else state = "upcoming";
        return (
          <div key={s.key} className="flex items-start shrink-0">
            <div className="flex flex-col items-center gap-1 w-24" data-testid={`cn-step-${s.key}-${state}`}>
              <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold border-2 ${
                state === "done" ? "bg-emerald-600 border-emerald-600 text-white"
                  : state === "current" ? "bg-white border-emerald-600 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-300"}`}>
                {state === "done" ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <p className={`text-[10px] text-center leading-tight ${state === "upcoming" ? "text-slate-400" : "text-slate-700 font-medium"}`}>{s.label}</p>
            </div>
            {i < STEP_DEFS.length - 1 && (
              <div className={`h-0.5 w-8 shrink-0 mt-3.5 ${i < currentIdx ? "bg-emerald-600" : "bg-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============ Settlement / Usage table ============
function SettlementTable({ cn, tipe, title, emptyMsg, canUpdate, canDelete, onAdd, onVoid }) {
  // Urutan bawaan: terbaru dulu. `settlements` adalah array NESTED di
  // dokumen Credit Note (bukan hasil query terpotong) — aman disortir tanpa
  // penanda "terpotong".
  const defaultRows = (cn.settlements || []).filter((s) => s.tipe === tipe).sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const { sortKey, sortDir, toggle } = useSortState(null);
  const rows = sortRows(defaultRows, sortKey, sortDir, {
    tanggal: (s) => s.tanggal || s.at || "",
    nominal: (s) => s.nominal || 0,
    tujuan: (s) => (tipe === "cair" ? s.akun_name : s.utang_code) || "",
    oleh: (s) => s.by || "",
  });
  const bisaTambah = STATUS_BOLEH_SETTLEMENT.includes(cn.status);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-base text-slate-900">{title}</p>
        {canUpdate && bisaTambah && (
          <button onClick={onAdd} data-testid={`cn-add-${tipe}`}
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" /> {tipe === "cair" ? "Catat Pencairan" : "Catat Pemakaian"}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">{emptyMsg}</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid={`cn-settle-sort-tanggal-${tipe}`} />
                <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid={`cn-settle-sort-nominal-${tipe}`} />
                <SortTh label={tipe === "cair" ? "Akun Tujuan" : "Utang"} sortKeyName="tujuan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid={`cn-settle-sort-tujuan-${tipe}`} />
                <th className="text-left px-3 py-2">Keterangan</th>
                <SortTh label="Oleh" sortKeyName="oleh" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid={`cn-settle-sort-oleh-${tipe}`} />
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((s) => (
                <tr key={s.id} data-testid={`cn-settle-row-${s.id}`} className={s.voided ? "opacity-50" : ""}>
                  <td className="px-3 py-2">{formatTanggal(s.tanggal)}</td>
                  <td className="px-3 py-2 text-right font-mono">{idr(s.nominal)}</td>
                  <td className="px-3 py-2">{tipe === "cair" ? s.akun_name : s.utang_code}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{s.keterangan || "-"}</td>
                  <td className="px-3 py-2 text-xs">{s.by_name || s.by}</td>
                  <td className="px-3 py-2 text-center">
                    {s.voided
                      ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Voided</span>
                      : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Aktif</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!s.voided && canDelete && (
                      <button onClick={() => onVoid(s)} data-testid={`cn-void-${s.id}`} className="text-rose-600 hover:text-rose-800 text-xs font-medium">Void</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ Info asal dokumen (PP-UANG) ============
// Selalu TEKS; tautan ke batch cashback hanya bagi pemegang izin lihat Cashback
// Claim (selain itu tautannya berujung 403). Menu Retur tidak punya halaman
// detail per retur, jadi asal retur cukup disebut kodenya.
function InfoAsal({ cn, asalCashback, asalRetur, bolehLihatCashback, navigate }) {
  if (!asalCashback.length && !asalRetur) return null;
  return (
    <div className="mt-4 space-y-2">
      {asalCashback.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900" data-testid="cn-info-asal-cashback">
          {asalCashback.length > 1 && (
            <p className="font-semibold text-rose-700 mb-1">
              Data ganda: CN ini tertaut ke {asalCashback.length} settlement Cashback sekaligus — tidak bisa dibatalkan otomatis. Hubungi Finance CEO.
            </p>
          )}
          {asalCashback.map((a) => (
            <p key={a.settlement_id} className="flex flex-wrap items-center gap-1">
              <span>Diterbitkan otomatis oleh settlement Cashback Claim</span>
              {bolehLihatCashback ? (
                <button onClick={() => navigate(`/finance/cashback/claims/${a.claim_id}`)}
                        data-testid={`cn-info-asal-cashback-link-${a.claim_id}`}
                        className="font-mono font-semibold text-sky-700 hover:text-sky-900 underline">{a.claim_code}</button>
              ) : (
                <span className="font-mono font-semibold">{a.claim_code}</span>
              )}
              {a.perlu_pemeriksaan && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">settlement-nya tertahan — perlu pemeriksaan</span>
              )}
            </p>
          ))}
          <p className="text-xs text-sky-800 mt-1">
            Membatalkan CN ini ikut mem-void settlement tersebut (nominalnya kembali outstanding di batch) — butuh izin hapus Credit Note DAN hapus Cashback Claim. Nilai & supplier CN mengikuti batch, tidak bisa diubah di sini.
          </p>
        </div>
      )}
      {asalRetur && asalRetur.sah && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="cn-info-asal-retur">
          CN ini kompensasi <b>kredit DOA</b> retur pembelian <span className="font-mono font-semibold">{asalRetur.retur_code}</span>.
          Nilainya {idr(cn.nilai)} SUDAH tercatat di Piutang CN (1260) oleh retur itu — Serahkan tidak menjurnal lagi.
          Untuk membatalkan atau mengubah nilainya, ubah kompensasinya di menu Retur Pembelian.
        </div>
      )}
      {asalRetur && !asalRetur.sah && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="cn-info-asal-retur">
          CN ini sisa kompensasi retur <span className="font-mono font-semibold">{asalRetur.retur_code}</span> yang sudah tidak berlaku (pemilihan kompensasinya gagal atau diubah) — tidak bisa diserahkan. Hapus atau batalkan CN ini.
        </div>
      )}
    </div>
  );
}

// ============ Modals aksi ============
function SerahkanModal({ cn, asalRetur, onClose, onDone }) {
  const [f, setF] = useState({ tanggal_diserahkan: hariIniISO(), expected_settlement_date: cn.expected_settlement_date || "", catatan: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/${cn.id}/serahkan`, {
        tanggal_diserahkan: f.tanggal_diserahkan,
        expected_settlement_date: f.expected_settlement_date || undefined,
        catatan: f.catatan,
      });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyerahkan Credit Note"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Serahkan ke Supplier" icon={Send} onClose={onClose} onSubmit={submit}
           submitLabel={asalRetur ? "Serahkan (tanpa jurnal)" : "Serahkan & Posting Jurnal"} busy={busy} testid="cn-serahkan-modal">
      {asalRetur ? (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800" data-testid="cn-serahkan-tanpa-jurnal">
          CN kompensasi retur {asalRetur.retur_code}: TIDAK ada jurnal baru — nilainya {idr(cn.nilai)} sudah tercatat di Piutang Credit Note Supplier (1260) saat kompensasi retur dipilih. Status saja yang berpindah.
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Aksi ini memposting jurnal CN terbit (D Piutang Credit Note Supplier 1260 / K Pendapatan Rebate Supplier 4910) sebesar {idr(cn.nilai)}.
        </div>
      )}
      <Field label="Tanggal Diserahkan"><DatePicker value={f.tanggal_diserahkan} onChange={(v) => setF({ ...f, tanggal_diserahkan: v })} data-testid="cn-serahkan-tgl" /></Field>
      <Field label="Expected Settlement (opsional)" hint="Kosongkan untuk otomatis +60 hari dari tanggal diserahkan">
        <DatePicker value={f.expected_settlement_date} onChange={(v) => setF({ ...f, expected_settlement_date: v })} data-testid="cn-serahkan-esd" />
      </Field>
      <Field label="Catatan (opsional)"><textarea rows={2} value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} data-testid="cn-serahkan-catatan" className={inputCls + " h-auto py-2"} /></Field>
    </Modal>
  );
}

function CairModal({ cn, onClose, onDone }) {
  const [kasBank, setKasBank] = useState([]);
  const [f, setF] = useState({ tanggal: hariIniISO(), nominal: cn.outstanding, akun_id: "", keterangan: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/finance/accounts/kas-bank").then((r) => setKasBank(r.data || [])).catch(() => {}); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/${cn.id}/settlement`, {
        tipe: "cair", nominal: Number(f.nominal), tanggal: f.tanggal, akun_id: f.akun_id, keterangan: f.keterangan,
      });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pencairan"); }
    finally { setBusy(false); }
  };
  const valid = Number(f.nominal) > 0 && Number(f.nominal) <= cn.outstanding + 0.5 && !!f.akun_id;
  return (
    // `dangerous` — nominal cair diketik/dipilih staf sendiri (InputRupiah).
    <Modal title="Catat Pencairan" icon={Wallet} onClose={onClose} onSubmit={submit} submitLabel="Simpan Pencairan" busy={busy} disabled={!valid} testid="cn-cair-modal"
           dangerous>
      <p className="text-sm text-slate-600">Outstanding saat ini: <b>{idr(cn.outstanding)}</b></p>
      <Field label="Tanggal"><DatePicker value={f.tanggal} onChange={(v) => setF({ ...f, tanggal: v })} data-testid="cn-cair-tgl" /></Field>
      <Field label="Nominal Cair"><InputRupiah value={f.nominal} onChange={(n) => setF({ ...f, nominal: n })} testid="cn-cair-nominal" className={inputCls} /></Field>
      <Field label="Masuk ke Akun">
        <select value={f.akun_id} onChange={(e) => setF({ ...f, akun_id: e.target.value })} data-testid="cn-cair-akun" className={inputCls}>
          <option value="">Pilih...</option>
          {kasBank.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="Keterangan (opsional)"><input value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cn-cair-ket" className={inputCls} /></Field>
      {Number(f.nominal) > cn.outstanding + 0.5 && <p className="text-xs text-rose-600">Nominal melebihi outstanding.</p>}
    </Modal>
  );
}

function TerpakaiModal({ cn, onClose, onDone }) {
  const [utangList, setUtangList] = useState([]);
  const [loadingUtang, setLoadingUtang] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ tanggal: hariIniISO(), nominal: "", keterangan: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/finance/utang", { params: { status: "aktif" } })
      .then((r) => setUtangList(r.data.items || []))
      .catch(() => toast.error("Gagal memuat daftar utang aktif"))
      .finally(() => setLoadingUtang(false));
  }, []);

  const filtered = useMemo(() => {
    const supplierLower = (cn.supplier_nama || "").toLowerCase();
    const term = search.trim().toLowerCase();
    return utangList
      .filter((u) => ((u.nominal || 0) - (u.nominal_terbayar || 0)) > 0.5)
      .filter((u) => {
        if (term) return (u.supplier_nama || "").toLowerCase().includes(term) || (u.code || "").toLowerCase().includes(term);
        return !supplierLower || (u.supplier_nama || "").toLowerCase().includes(supplierLower);
      })
      .slice(0, 30);
  }, [utangList, search, cn.supplier_nama]);

  const pilih = (u) => {
    setSelected(u);
    const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
    setF((prev) => ({ ...prev, nominal: Math.min(sisa, cn.outstanding) }));
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/${cn.id}/settlement`, {
        tipe: "terpakai", nominal: Number(f.nominal), tanggal: f.tanggal, utang_id: selected.id, keterangan: f.keterangan,
      });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pemakaian"); }
    finally { setBusy(false); }
  };

  const sisaTerpilih = selected ? (selected.nominal || 0) - (selected.nominal_terbayar || 0) : 0;
  const valid = !!selected && Number(f.nominal) > 0 && Number(f.nominal) <= cn.outstanding + 0.5 && Number(f.nominal) <= sisaTerpilih + 0.5;

  return (
    // `dangerous` — pencarian & pemilihan utang dinamis plus nominal yang diketik
    // staf sendiri; kalau tertutup tak sengaja pilihan & angka hilang semua.
    <Modal title="Catat Pemakaian (Potong Utang)" icon={ArrowLeftRight} onClose={onClose} onSubmit={submit}
           submitLabel="Terapkan ke Utang" busy={busy} disabled={!valid} testid="cn-terpakai-modal" wide
           dangerous>
      <p className="text-sm text-slate-600">Outstanding CN saat ini: <b>{idr(cn.outstanding)}</b></p>
      <Field label="Cari Utang Aktif (nama supplier / kode)">
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cn-terpakai-search" className={inputCls} placeholder={cn.supplier_nama} />
      </Field>
      <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
        {loadingUtang ? (
          <p className="p-3 text-xs text-slate-400">Memuat utang aktif...</p>
        ) : filtered.length === 0 ? (
          <p className="p-3 text-xs text-slate-400">Tidak ada utang aktif yang cocok. Coba ubah kata kunci pencarian.</p>
        ) : filtered.map((u) => {
          const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
          return (
            <button type="button" key={u.id} onClick={() => pilih(u)} data-testid={`cn-terpakai-utang-${u.id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${selected?.id === u.id ? "bg-emerald-50" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900 truncate">{u.supplier_nama}</span>
                <span className="font-mono text-xs text-slate-700 shrink-0">{idr(sisa)}</span>
              </div>
              <p className="text-[11px] text-slate-500">{u.code} · JT {u.tanggal_jatuh_tempo || "-"}</p>
            </button>
          );
        })}
      </div>
      {selected && (
        <>
          <Field label="Tanggal"><DatePicker value={f.tanggal} onChange={(v) => setF({ ...f, tanggal: v })} data-testid="cn-terpakai-tgl" /></Field>
          <Field label="Nominal Diterapkan" hint={`Sisa utang terpilih: ${idr(sisaTerpilih)}`}>
            <InputRupiah value={f.nominal} onChange={(n) => setF({ ...f, nominal: n })} testid="cn-terpakai-nominal" className={inputCls} />
          </Field>
          <Field label="Keterangan (opsional)"><input value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cn-terpakai-ket" className={inputCls} /></Field>
        </>
      )}
    </Modal>
  );
}

function FollowUpModal({ cn, onClose, onDone }) {
  const [f, setF] = useState({ tanggal: hariIniISO(), metode: "Telepon", pic: "", hasil: "", next_follow_up_date: "", catatan: "" });
  const [busy, setBusy] = useState(false);
  const valid = f.pic.trim() && f.hasil.trim();
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/${cn.id}/follow-up`, { ...f, next_follow_up_date: f.next_follow_up_date || undefined });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat follow-up"); }
    finally { setBusy(false); }
  };
  return (
    // `dangerous` — form panjang (6 field termasuk PIC & hasil tulisan bebas)
    // yang gampang hilang kalau modal tertutup tak sengaja sebelum disimpan.
    <Modal title="Tambah Follow-up" icon={PhoneCall} onClose={onClose} onSubmit={submit} submitLabel="Simpan Follow-up" busy={busy} disabled={!valid} testid="cn-followup-modal"
           dangerous>
      <Field label="Tanggal"><DatePicker value={f.tanggal} onChange={(v) => setF({ ...f, tanggal: v })} data-testid="cn-fu-tgl" /></Field>
      <Field label="Metode">
        <select value={f.metode} onChange={(e) => setF({ ...f, metode: e.target.value })} data-testid="cn-fu-metode" className={inputCls}>
          {METODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <Field label="PIC"><input value={f.pic} onChange={(e) => setF({ ...f, pic: e.target.value })} data-testid="cn-fu-pic" className={inputCls} /></Field>
      <Field label="Hasil"><textarea rows={2} value={f.hasil} onChange={(e) => setF({ ...f, hasil: e.target.value })} data-testid="cn-fu-hasil" className={inputCls + " h-auto py-2"} /></Field>
      <Field label="Follow-up Berikutnya (opsional)"><DatePicker value={f.next_follow_up_date} onChange={(v) => setF({ ...f, next_follow_up_date: v })} data-testid="cn-fu-next" /></Field>
      <Field label="Catatan (opsional)"><textarea rows={2} value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} data-testid="cn-fu-catatan" className={inputCls + " h-auto py-2"} /></Field>
    </Modal>
  );
}

function CloseModal({ cn, onClose, onDone }) {
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/${cn.id}/close`, { catatan });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menutup Credit Note"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Tutup Credit Note" icon={Lock} onClose={onClose} onSubmit={submit} submitLabel="Tutup (Closed)" busy={busy} testid="cn-close-modal">
      <p className="text-sm text-slate-600">Outstanding CN ini {idr(cn.outstanding)}. Menutup akan menandainya selesai dan tidak lagi dipantau di aging.</p>
      <Field label="Catatan (opsional)"><textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="cn-close-catatan" className={inputCls + " h-auto py-2"} /></Field>
    </Modal>
  );
}

function EditModal({ cn, kategoriOpts, cabangOptions, bertaut, onClose, onDone }) {
  const sudahTerbit = !!cn.tanggal_diserahkan;
  // CN bertaut (settlement cashback / kompensasi retur): nilai & supplier milik
  // dokumen asalnya — backend menolak perubahan. Dikunci & tidak dikirim, sama
  // seperti CN yang sudah terbit.
  const kunciNilai = sudahTerbit || !!bertaut;
  const [f, setF] = useState({
    supplier_nama: cn.supplier_nama, brand: cn.brand || "", cabang: cn.cabang || "",
    kategori: cn.kategori, nilai: cn.nilai, no_referensi: cn.no_referensi || "",
    keterangan: cn.keterangan || "", expected_settlement_date: cn.expected_settlement_date || "",
    tanggal_terbit: cn.tanggal_terbit || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        brand: f.brand, cabang: f.cabang, kategori: f.kategori, no_referensi: f.no_referensi,
        keterangan: f.keterangan, expected_settlement_date: f.expected_settlement_date || null,
        tanggal_terbit: f.tanggal_terbit,
      };
      // KENAPA dua field ini dikeluarkan dari body sama sekali (bukan cuma
      // di-disable di UI): backend menolak PUT kalau `nilai`/`supplier_nama`
      // TERKIRIM sama sekali (bukan None) begitu CN sudah diserahkan — lihat
      // `update_credit_note` di credit_note.py. Mengirim nilai lama yang sama
      // pun tetap ditolak, jadi field-nya harus benar-benar tidak ada di JSON.
      if (!kunciNilai) { body.supplier_nama = f.supplier_nama; body.nilai = Number(f.nilai); }
      const r = await api.put(`${CN_BASE}/${cn.id}`, body);
      toast.success(r.data.message || "Credit Note diperbarui ✓"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memperbarui Credit Note"); }
    finally { setBusy(false); }
  };
  return (
    // `dangerous` — form panjang (9 field) termasuk nilai CN (InputRupiah)
    // diketik staf sendiri saat belum terbit.
    <Modal title="Edit Credit Note" icon={Pencil} onClose={onClose} onSubmit={submit} submitLabel="Simpan Perubahan" busy={busy} testid="cn-edit-modal" wide
           dangerous>
      {sudahTerbit ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          CN sudah diserahkan — Nama Supplier & Nilai tidak bisa diubah lagi (jurnal sudah mencatat nilai lama).
        </div>
      ) : bertaut ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="cn-edit-kunci-bertaut">
          Nama Supplier & Nilai CN ini mengikuti dokumen asalnya (settlement Cashback / kompensasi retur) — tidak bisa diubah di sini.
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nama Supplier" col2>
          <input value={f.supplier_nama} disabled={kunciNilai} onChange={(e) => setF({ ...f, supplier_nama: e.target.value })} data-testid="cn-edit-supplier" className={inputCls} />
        </Field>
        <Field label="Brand"><input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="cn-edit-brand" className={inputCls} /></Field>
        <Field label="Cabang">
          <select value={f.cabang} onChange={(e) => setF({ ...f, cabang: e.target.value })} data-testid="cn-edit-cabang" className={inputCls}>
            {cabangOptions.map((c) => <option key={c || "_none"} value={c}>{c || "-"}</option>)}
          </select>
        </Field>
        <Field label="Kategori">
          <select value={f.kategori} onChange={(e) => setF({ ...f, kategori: e.target.value })} data-testid="cn-edit-kategori" className={inputCls}>
            {kategoriOpts.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Nilai CN">
          <InputRupiah value={f.nilai} onChange={(n) => setF({ ...f, nilai: n })} disabled={kunciNilai} testid="cn-edit-nilai" className={inputCls} />
        </Field>
        <Field label="Tanggal Terbit"><DatePicker value={f.tanggal_terbit} onChange={(v) => setF({ ...f, tanggal_terbit: v })} data-testid="cn-edit-tgl-terbit" /></Field>
        <Field label="No. Referensi"><input value={f.no_referensi} onChange={(e) => setF({ ...f, no_referensi: e.target.value })} data-testid="cn-edit-noref" className={inputCls} /></Field>
        <Field label="Expected Settlement"><DatePicker value={f.expected_settlement_date} onChange={(v) => setF({ ...f, expected_settlement_date: v })} data-testid="cn-edit-esd" /></Field>
        <Field label="Keterangan" col2><textarea rows={2} value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cn-edit-ket" className={inputCls + " h-auto py-2"} /></Field>
      </div>
    </Modal>
  );
}

function ReasonModal({ title, icon, label, submitLabel, onClose, onSubmit, testid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} icon={icon} tone="rose" onClose={onClose} onSubmit={submit} submitLabel={submitLabel} busy={busy} disabled={!reason.trim()} testid={testid}>
      <Field label={label}>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-input`} className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}
